import { ref, onUnmounted } from 'vue';

// Global state shared across all components
const currentTime = ref(Date.now());
const subscribers = new Set();
let intervalId = null;

// Control flags for race conditions
let isStarting = false;
let isStopping = false;

// Timer update interval (30 seconds)
const TIMER_UPDATE_INTERVAL_MS = 30000;

/**
 * Starts the global timer if it's not already running
 */
function startTimer() {
  if (isStarting || intervalId !== null) return;
  if (subscribers.size === 0) return;

  isStarting = true;
  intervalId = setInterval(() => {
    currentTime.value = Date.now();
  }, TIMER_UPDATE_INTERVAL_MS);
  isStarting = false;
}

/**
 * Stops the global timer if no subscribers are left
 */
function stopTimer() {
  if (isStopping || intervalId === null) return;
  if (subscribers.size > 0) return;

  isStopping = true;
  clearInterval(intervalId);
  intervalId = null;
  isStopping = false;
}

/**
 * Global timer composable that manages a single timer instance
 * shared across all components that need time updates
 *
 * @returns {Object} - Object containing currentTime ref and cleanup function
 */
export default function useGlobalTimer() {
  const subscriberId = Symbol('timer-subscriber');

  subscribers.add(subscriberId);
  startTimer();

  const cleanup = () => {
    if (subscribers.has(subscriberId)) {
      subscribers.delete(subscriberId);
    }
    stopTimer();
  };

  onUnmounted(cleanup);

  return {
    currentTime,
    cleanup,
  };
}
